// packages/kern/src/layout.ts — Spalten und Geometrie (SPEC §3.4, 1:1 portiert).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten.
//
// Die Konstanten stehen EXAKT so, in dieser Datei und nirgends sonst. Jede
// Zahl ist Druckbild (T8, §3.15).
//
// Die Rümpfe sind wortgleich aus `docs/quellen/understand/prototype.json`,
// reusable_snippets[9] („buildCols() + layout() + miToX/xToMi …"), mit den
// Übersetzungen aus §3.4: der globale Zustand `S`/`L` des Prototyps wird zum
// Parameter, `lanes` (Ergebnisliste) heißt `spuren`, `lane.rows` (Array) heißt
// `spur.reihen`, `byId` heißt `nachId`.

import { TB } from "./texte";
import type { Layout, LSpur, Mi, Plan, Spalte } from "./types";
import { parseYM } from "./zeit";

export const PAD_X = 22, PAD_TOP = 16, PAD_BOT = 16;
export const LABEL_W = 30, LABEL_GAP = 10;
export const YEAR_H = 20, MONTH_H = 30;
export const LANE_GAP = 16, ROW_GAP = 6, ICON_H = 28;
export const FER_GAP = 18, FER_H = 26, FER_LABEL_W = 112;

/** 1 mm in px bei 96 dpi. Damit ist 1 px genau 1 mm und der Export kann mm ausweisen. */
export const MM = 96 / 25.4;

/** Blattmasse der festen Formate in Millimetern, quer. */
export const BLATT: Record<"a4q" | "a3q", { readonly w: number; readonly h: number; readonly label: string }> = {
  a4q: { w: 297, h: 210, label: "A4 quer" },
  a3q: { w: 420, h: 297, label: "A3 quer" },
};

/** Unter dieser Balkenhoehe wird der Satz im Druck unbrauchbar (Hinweis, keine Sperre). */
export const ZEILE_MIN_LESBAR = 26;

/**
 * Grenzen der ERRECHNETEN Zeilenhoehe bei `fitRows`. Jede Zahl hier ist
 * Druckbild (T8, §3.15) und steht deshalb im Vertrag, nicht im Rumpf.
 *
 * Nach unten 14 px (≈ 3,7 mm): darunter traegt der Balken keine Schrift
 * mehr, und das Layout meldet lieber `ueberlauf`, als unlesbar zu
 * schrumpfen. Nach oben 76 px, damit ein Blatt mit zwei Zeilen keine
 * handbreiten Balken bekommt.
 */
export const ZEILE_FIT_MIN = 14, ZEILE_FIT_MAX = 76;

/** Grenzen und Vorgabe des Rands in mm. DIESELBEN Zahlen wie in `migriere()`. */
export const RAND_MIN_MM = 3, RAND_MAX_MM = 25, RAND_VORGABE_MM = 8;

export const MON = ["Jan","Feb","Mrz","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"] as const;
export const MONLONG = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"] as const;
export const BLAENDER: ReadonlyArray<readonly [string, string]> = [
  ["BW","Baden-Württemberg"],["BY","Bayern"],["BE","Berlin"],["BB","Brandenburg"],
  ["HB","Bremen"],["HH","Hamburg"],["HE","Hessen"],["MV","Mecklenburg-Vorpommern"],
  ["NI","Niedersachsen"],["NW","Nordrhein-Westfalen"],["RP","Rheinland-Pfalz"],
  ["SL","Saarland"],["SN","Sachsen"],["ST","Sachsen-Anhalt"],
  ["SH","Schleswig-Holstein"],["TH","Thüringen"],
];

/**
 * Spalten des Blatts: ein Eintrag je Monat, ab `compressFrom` je Quartal.
 * `x` und `w` bleiben hier 0 — sie werden erst in `layout()` gesetzt.
 *
 * Die Zählbremse `guard++ < 600` ist Absicht: ein Plan mit unsinnigem
 * Zeitraum (alter Import, vertippte Jahreszahl) soll ein zu breites Blatt
 * ergeben und NICHT den Browser anhalten.
 */
export function buildCols(plan: Plan): Spalte[] {
  const a = parseYM(plan.start), b = parseYM(plan.end) + 1;
  const c = plan.compressFrom ? parseYM(plan.compressFrom) : Infinity;
  const cols: Spalte[] = [];
  let i = a;
  let guard = 0;
  while (i < b && guard++ < 600) {
    if (i >= c) {
      const y = Math.floor(i / 12), q = Math.floor((i % 12) / 3);
      const qs = y * 12 + q * 3, qe = qs + 3;
      const s = Math.max(qs, i), e = Math.min(qe, b);
      cols.push({ s, e, kind: "q", label: TB.quartal(q + 1), wt: plan.compressW, year: y, x: 0, w: 0 });
      i = e;
    } else {
      cols.push({ s: i, e: i + 1, kind: "m", label: MON[((i % 12) + 12) % 12]!, wt: 1, year: Math.floor(i / 12), x: 0, w: 0 });
      i++;
    }
  }
  return cols;
}

/**
 * Das ganze Gerüst des Blatts in einem Durchgang: Spaltenbreiten, Kopfzonen,
 * Zeilenhöhen je Spur und die Gesamthöhe.
 *
 * Ein Piktogramm in EINER Zeile macht diese Zeile `ICON_H` höher und schiebt
 * damit alles darunter nach unten. Das nicht zu „glätten" ist Absicht — es ist
 * das Druckbild (§3.4).
 */
export function layout(plan: Plan): Layout {
  // Zwei Blattmodelle, ein Rechenweg (T25a). Bei `frei` bleibt alles wie im
  // Prototyp: `pad` ist PAD_X, die Hoehe waechst mit dem Inhalt. Bei a4q/a3q
  // stehen Breite UND Hoehe fest, der Rand kommt aus `plan.margin`, und die
  // Ferienleiste sitzt auf der unteren Blattkante statt unter dem Inhalt.
  const fest = plan.format === "a4q" || plan.format === "a3q";
  const blatt = fest ? BLATT[plan.format as "a4q" | "a3q"] : null;
  const W = blatt ? Math.round(blatt.w * MM) : plan.width;
  // PAD_X (22) und PAD_TOP (16) sind VERSCHIEDEN. Beim festen Blatt ist der
  // Rand umlaufend gleich, bei `frei` bleiben es die beiden Zahlen des
  // Prototyps — sie zusammenzuziehen verschoebe den Titel um 6 px.
  // `plan.margin` kommt NICHT immer aus migriere(): der Ladeweg im Speicher
  // nimmt `voll.daten` unveraendert aus der API (usePlanStore, „let plan =
  // voll.daten"), und die drei Saatvorlagen in 002_seed_vorlagen.sql tragen
  // das Feld nicht. Ein Bestandsplan, den jemand auf a4q stellt, haette hier
  // also `undefined` — `Math.max(2, undefined)` ist NaN, und von da an ist
  // JEDE Zahl im Blatt NaN: leeres SVG, leeres PDF, keine Fehlermeldung.
  // Darum die Vorgabe genau hier, an der einen Stelle, die Browser UND Export
  // teilen; die Grenzen sind die von migriere() (3…25 mm).
  const randMm = Number.isFinite(plan.margin)
    ? Math.min(RAND_MAX_MM, Math.max(RAND_MIN_MM, plan.margin))
    : RAND_VORGABE_MM;
  const pad = blatt ? randMm * MM : PAD_X;
  const padTop = blatt ? pad : PAD_TOP;

  const cols = buildCols(plan);
  const x0 = pad + LABEL_W + LABEL_GAP, x1 = W - pad;
  const total = cols.reduce((s, c) => s + c.wt, 0) || 1;
  const unit = (x1 - x0) / total;
  let cx = x0;
  cols.forEach((c) => { c.x = cx; c.w = c.wt * unit; cx += c.w; });

  const titleH = plan.title ? 30 : 0;
  const yearY = padTop + titleH + 2;
  const monthY = yearY + YEAR_H + 6;
  const bodyY = monthY + MONTH_H + 8;

  const spuren: LSpur[] = plan.lanes.map((l) => ({ id: l.id, name: l.name, def: l.def, reihen: [], y: 0, h: 0 }));
  const nachId: Record<string, LSpur> = {};
  spuren.forEach((s) => { nachId[s.id] = s; });

  // Erst das Zeilenraster ERMITTELN (welche Zeile traegt ein Piktogramm), dann
  // die Balkenhoehe bestimmen — bei `fitRows` haengt sie davon ab, wie viel
  // Platz die Piktogrammzeilen schon wegnehmen.
  const iconH: number[][] = plan.lanes.map((cfg) => {
    // `|0` ist der Schutz gegen `rows: 2.5` aus einem alten Import (§3.4).
    const n = Math.max(1, cfg.rows | 0);
    const out: number[] = [];
    for (let r = 0; r < n; r++) {
      // Nur ein Kasten ab einer GANZEN Zeile reserviert die Zone darüber. Ab
      // einer halben Zeile steht das Symbol im Kasten selbst (T43) — sonst
      // bliebe über der Zeile ein leerer Streifen.
      const hasIcon = plan.items.some((it) => it.lane === cfg.id && it.row === r && !!it.icon && it.icon !== "none"
        && (it.icon !== "eigen" || !!it.eigenesIcon));
      out.push(hasIcon ? ICON_H : 0);
    }
    return out;
  });

  const hFest = blatt ? Math.round(blatt.h * MM) : 0;
  const ferYFest = hFest - pad - FER_H;
  const bodyAvail = blatt ? (plan.ferien ? ferYFest - FER_GAP : hFest - pad) - bodyY : 0;

  const nReihen = iconH.reduce((a, x) => a + x.length, 0) || 1;
  const summeIcon = iconH.reduce((a, x) => a + x.reduce((u, v) => u + v, 0), 0);
  const gaps = Math.max(0, plan.lanes.length - 1) * LANE_GAP;

  let rowH = plan.rowH;
  if (blatt && plan.fitRows) {
    rowH = Math.max(ZEILE_FIT_MIN, Math.min(ZEILE_FIT_MAX, (bodyAvail - summeIcon - gaps) / nReihen - ROW_GAP));
  }
  const gebraucht = nReihen * (rowH + ROW_GAP) + summeIcon + gaps;
  const ueberlauf = !!blatt && gebraucht > bodyAvail + 0.5;

  plan.lanes.forEach((_cfg, li) => {
    const spur = spuren[li]!;
    (iconH[li] ?? []).forEach((ic, r) => {
      spur.reihen.push({ idx: r, iconH: ic, h: rowH + ROW_GAP + ic, y: 0 });
    });
  });

  let y = bodyY;
  spuren.forEach((spur, i) => {
    spur.y = y;
    spur.reihen.forEach((r) => { r.y = y; y += r.h; });
    spur.h = y - spur.y;
    if (i < spuren.length - 1) y += LANE_GAP;
  });

  const bodyBottom = y;
  // Bei festem Blatt sitzt die Ferienleiste auf der unteren Kante, nicht
  // direkt unter dem Inhalt — sonst schwebt sie bei kurzen Plaenen mitten
  // auf der Seite.
  const ferY = blatt ? ferYFest : (plan.ferien ? bodyBottom + FER_GAP : bodyBottom);
  const height = blatt
    ? hFest
    : Math.round((plan.ferien ? ferY + FER_H : bodyBottom) + PAD_BOT);

  return { W, height, pad, padTop, rowH, ueberlauf, cols, x0, x1, yearY, monthY, bodyY, bodyBottom, ferY, spuren, nachId, titleH };
}

/**
 * Monatsindex → Pixel. SPALTENWEISE linear, NICHT zeitlinear: sobald
 * `compressFrom` gesetzt ist, ist ein Monat im Quartalsblock nur `compressW/3`
 * so breit wie ein Einzelmonat. Jede eigene Zeit-zu-Pixel-Rechnung irgendwo im
 * Frontend ist ein Fehler — es gibt genau diese zwei Funktionen.
 */
export function miToX(L: Layout, v: Mi): number {
  const c = L.cols;
  if (!c.length) return L.x0;
  if (v <= c[0]!.s) return L.x0;
  const last = c[c.length - 1]!;
  if (v >= last.e) return L.x1;
  for (let i = 0; i < c.length; i++) {
    const cc = c[i]!;
    if (v < cc.e) return cc.x + (v - cc.s) / (cc.e - cc.s) * cc.w;
  }
  return L.x1;
}

/** Pixel → Monatsindex, die Umkehrung von `miToX` innerhalb des Blatts. */
export function xToMi(L: Layout, x: number): Mi {
  const c = L.cols;
  if (!c.length) return 0;
  if (x <= L.x0) return c[0]!.s;
  for (let i = 0; i < c.length; i++) {
    const cc = c[i]!;
    if (x < cc.x + cc.w) return cc.s + (x - cc.x) / cc.w * (cc.e - cc.s);
  }
  return c[c.length - 1]!.e;
}


// ----------------------------------------------------------- Halbe Zeilen (T42)

/** Das senkrechte Raster: halbe Zeilen. */
export const HALBZEILE = 0.5;

/** Auf halbe Zeilen runden. `Math.round` — Server rechnet `floor(x + 0,5)`, dasselbe. */
export const aufHalbe = (v: number): number => Math.round(v * 2) / 2;

/**
 * Ober- und Unterkante eines Kastens, der bei `row` beginnt und `hoehe` Zeilen
 * hoch ist — beides in halben Zeilen (T42).
 *
 * Jede Zeile hat unter ihrer Piktogrammzone eine Balkenzone `[T, T + rowH]`.
 * Die zerfällt in zwei Halbzonen mit ROW_GAP dazwischen:
 *
 *     oben  [T, T + h]        unten [T + h + ROW_GAP, T + rowH]     h = (rowH − ROW_GAP) / 2
 *
 * Ein Kasten über GENAU eine ganze Zeile ist damit die bisherige Balkenzone
 * `[T, T + rowH]` — deshalb bleibt jeder Plan mit ganzen Zeilen pixelgleich,
 * und die Goldens gelten weiter. Geht ein Kasten über mehrere Zeilen, überdeckt
 * er die Lücken und Piktogrammzonen dazwischen.
 *
 * Neben `miToX`/`xToMi` die EINE Stelle, die Zeilen in Pixel umrechnet.
 */
export function kastenKanten(L: Layout, spur: LSpur, row: number, hoehe: number): { y0: number; y1: number } {
  const n = spur.reihen.length;
  const halb = (L.rowH - ROW_GAP) / 2;
  const h = Number.isFinite(hoehe) && hoehe > 0 ? Math.max(HALBZEILE, hoehe) : 1;
  const r = Number.isFinite(row) && row > 0 ? row : 0;
  // Halbzonen durchnummeriert: s = 2 * Zeile + (0 oben | 1 unten).
  const s0 = Math.max(0, Math.min(2 * n - 1, Math.round(r * 2)));
  const s1 = Math.max(s0, Math.min(2 * n - 1, Math.round((r + h) * 2) - 1));
  const T = (s: number) => {
    const reihe = spur.reihen[s >> 1]!;
    return reihe.y + reihe.iconH;
  };
  return {
    y0: (s0 & 1) ? T(s0) + halb + ROW_GAP : T(s0),
    y1: (s1 & 1) ? T(s1) + L.rowH : T(s1) + halb,
  };
}

/**
 * Welche halbe Zeile liegt unter `y`? Die Grenze ist die Mitte der
 * Balkenzone; die Piktogrammzone zählt zur oberen Hälfte. `null` außerhalb
 * aller Spuren.
 */
export function halbzeileAn(L: Layout, y: number): { lane: string; row: number } | null {
  for (const spur of L.spuren) {
    for (const r of spur.reihen) {
      if (y >= r.y && y < r.y + r.h) {
        const mitte = r.y + r.iconH + L.rowH / 2;
        return { lane: spur.id, row: r.idx + (y >= mitte ? HALBZEILE : 0) };
      }
    }
  }
  return null;
}
